import React, { forwardRef, useState } from 'react';
import classNames from 'classnames';
import { FiEye, FiEyeOff } from 'react-icons/fi';

interface TextFieldProps
  extends React.AllHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  isRequired?: boolean;
  containerClassName?: string;
  multiline?: boolean;
  rows?: number;
}

const TextField = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  TextFieldProps
>(
  (
    {
      label,
      error,
      helperText,
      leftIcon,
      rightIcon,
      isRequired,
      containerClassName,
      className,
      type = 'text',
      disabled,
      multiline,
      rows = 4,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === 'password';

    const inputType = isPassword ? (showPassword ? 'text' : 'password') : type;

    const commonClasses = classNames(
      'w-full px-4 py-2.5 rounded-lg border transition-all duration-200',
      'focus:outline-none focus:ring-2',
      'placeholder:text-blue-mid-9 text-sm focus:border-purple-600',
      {
        'pl-10': leftIcon,
        'pr-10': rightIcon || isPassword,
        'border-gray-300 focus:border-purple-600 focus:ring-purple-100':
          !error || error,
        'bg-gray-100 cursor-not-allowed text-gray-600': disabled,
      },
      className
    );

    return (
      <div className={classNames('w-full', containerClassName)}>
        {label && (
          <label className="block mb-1.5 text-sm font-medium text-gray-800">
            {label}
            {isRequired && <span className="text-danger ml-1">*</span>}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600">
              {leftIcon}
            </div>
          )}
          {multiline ? (
            <textarea
              ref={ref as React.Ref<HTMLTextAreaElement>}
              rows={rows}
              disabled={disabled}
              className={classNames(commonClasses, 'resize-none')}
              {...(props as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
            />
          ) : (
            <input
              ref={ref as React.Ref<HTMLInputElement>}
              type={inputType}
              disabled={disabled}
              className={commonClasses}
              {...(props as React.InputHTMLAttributes<HTMLInputElement>)}
            />
          )}

          {isPassword && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800 focus:outline-none"
              tabIndex={-1}
            >
              {showPassword ? <FiEye size={20} /> : <FiEyeOff size={20} />}
            </button>
          )}
          {rightIcon && !isPassword && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600">
              {rightIcon}
            </div>
          )}
        </div>
        {(error || helperText) && (
          <p
            className={classNames('mt-1.5 text-xs', {
              'text-danger': error,
              'text-gray-600': !error && helperText,
            })}
          >
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

TextField.displayName = 'TextField';

export default TextField;
